"use client";

import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useImpersonate } from "@/hooks/auth/useImpersonate";
import { $api } from "@/lib/api-client";
import { cn } from "@/lib/utils";

/**
 * Panel for DEV users to impersonate other users.
 * Shows a wallet address input with debounced user lookup,
 * displays the found user's nickname, and provides an impersonate button.
 *
 * Visible only when user.role === 'DEV' and not currently impersonating.
 */
export const ImpersonationPanel: React.FC = () => {
  const t = useTranslations("auth");
  const [walletInput, setWalletInput] = useState("");
  const [debouncedWallet, setDebouncedWallet] = useState("");
  const impersonateMutation = useImpersonate();

  // Debounce the wallet input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedWallet(walletInput.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [walletInput]);

  const shouldLookup = debouncedWallet.length > 5;

  // Lookup user by wallet address
  const userLookup = $api.useQuery(
    "get",
    "/user/wallet/{wallet_address}",
    {
      params: {
        path: { wallet_address: debouncedWallet },
      },
    },
    {
      enabled: shouldLookup,
      retry: false,
    },
  );

  const foundUser = userLookup.data?.data;
  const isLoading = userLookup.isLoading && shouldLookup;
  const isNotFound = userLookup.isError && shouldLookup && !isLoading;

  const handleImpersonate = useCallback(() => {
    if (foundUser?.wallet_address) {
      impersonateMutation.mutate(foundUser.wallet_address);
    }
  }, [foundUser?.wallet_address, impersonateMutation]);

  return (
    <div
      className={cn(
        "w-full rounded-lg border border-stroke-light",
        "bg-bg-secondary p-4 space-y-3",
      )}
    >
      <h3 className="text-sm font-semibold text-text-primary">
        {t("impersonation.title")}
      </h3>

      <div className="space-y-2">
        <label
          htmlFor="impersonate-wallet"
          className="text-xs text-text-secondary"
        >
          {t("impersonation.walletAddressLabel")}
        </label>
        <Input
          id="impersonate-wallet"
          dir="ltr"
          placeholder="0x..."
          value={walletInput}
          onChange={(e) => setWalletInput(e.target.value)}
          className="text-xs font-mono"
        />
      </div>

      {/* Loading state */}
      {isLoading && (
        <p className="text-xs text-text-secondary">
          {t("impersonation.searching")}
        </p>
      )}

      {/* User found */}
      {foundUser && !isLoading && (
        <div className="flex items-center justify-between rounded-md bg-bg-primary p-2">
          <span className="text-sm text-text-primary">
            {foundUser.nickname ?? foundUser.wallet_address}
          </span>
        </div>
      )}

      {/* Not found */}
      {isNotFound && !isLoading && (
        <p className="text-xs text-semantic-error">
          {t("impersonation.userNotFound")}
        </p>
      )}

      <Button
        type="button"
        variant="solid"
        colors="secondary"
        className="w-full"
        disabled={!foundUser || impersonateMutation.isPending}
        loading={impersonateMutation.isPending}
        onClick={handleImpersonate}
      >
        {t("impersonation.viewAccount")}
      </Button>
    </div>
  );
};
